import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const VERSIONS = [
  {
    key: 'ARC',
    label: 'ARC',
    url: 'https://raw.githubusercontent.com/damarals/biblias/master/inst/json/ARC.json',
  },
  {
    key: 'KJA',
    label: 'KJA',
    url: 'https://raw.githubusercontent.com/damarals/biblias/master/inst/json/KJA.json',
  },
  {
    key: 'NAA',
    label: 'NAA',
    url: 'https://raw.githubusercontent.com/damarals/biblias/master/inst/json/NAA.json',
  },
];

const BOOKS = [
  { name: 'Gênesis', chapters: 50, testament: 'AT' },
  { name: 'Êxodo', chapters: 40, testament: 'AT' },
  { name: 'Levítico', chapters: 27, testament: 'AT' },
  { name: 'Números', chapters: 36, testament: 'AT' },
  { name: 'Deuteronômio', chapters: 34, testament: 'AT' },
  { name: 'Josué', chapters: 24, testament: 'AT' },
  { name: 'Juízes', chapters: 21, testament: 'AT' },
  { name: 'Rute', chapters: 4, testament: 'AT' },
  { name: '1 Samuel', chapters: 31, testament: 'AT' },
  { name: '2 Samuel', chapters: 24, testament: 'AT' },
  { name: '1 Reis', chapters: 22, testament: 'AT' },
  { name: '2 Reis', chapters: 25, testament: 'AT' },
  { name: '1 Crônicas', chapters: 29, testament: 'AT' },
  { name: '2 Crônicas', chapters: 36, testament: 'AT' },
  { name: 'Esdras', chapters: 10, testament: 'AT' },
  { name: 'Neemias', chapters: 13, testament: 'AT' },
  { name: 'Ester', chapters: 10, testament: 'AT' },
  { name: 'Jó', chapters: 42, testament: 'AT' },
  { name: 'Salmos', chapters: 150, testament: 'AT' },
  { name: 'Provérbios', chapters: 31, testament: 'AT' },
  { name: 'Eclesiastes', chapters: 12, testament: 'AT' },
  { name: 'Cânticos', chapters: 8, testament: 'AT' },
  { name: 'Isaías', chapters: 66, testament: 'AT' },
  { name: 'Jeremias', chapters: 52, testament: 'AT' },
  { name: 'Lamentações', chapters: 5, testament: 'AT' },
  { name: 'Ezequiel', chapters: 48, testament: 'AT' },
  { name: 'Daniel', chapters: 12, testament: 'AT' },
  { name: 'Oseias', chapters: 14, testament: 'AT' },
  { name: 'Joel', chapters: 3, testament: 'AT' },
  { name: 'Amós', chapters: 9, testament: 'AT' },
  { name: 'Obadias', chapters: 1, testament: 'AT' },
  { name: 'Jonas', chapters: 4, testament: 'AT' },
  { name: 'Miqueias', chapters: 7, testament: 'AT' },
  { name: 'Naum', chapters: 3, testament: 'AT' },
  { name: 'Habacuque', chapters: 3, testament: 'AT' },
  { name: 'Sofonias', chapters: 3, testament: 'AT' },
  { name: 'Ageu', chapters: 2, testament: 'AT' },
  { name: 'Zacarias', chapters: 14, testament: 'AT' },
  { name: 'Malaquias', chapters: 4, testament: 'AT' },
  { name: 'Mateus', chapters: 28, testament: 'NT' },
  { name: 'Marcos', chapters: 16, testament: 'NT' },
  { name: 'Lucas', chapters: 24, testament: 'NT' },
  { name: 'João', chapters: 21, testament: 'NT' },
  { name: 'Atos', chapters: 28, testament: 'NT' },
  { name: 'Romanos', chapters: 16, testament: 'NT' },
  { name: '1 Coríntios', chapters: 16, testament: 'NT' },
  { name: '2 Coríntios', chapters: 13, testament: 'NT' },
  { name: 'Gálatas', chapters: 6, testament: 'NT' },
  { name: 'Efésios', chapters: 6, testament: 'NT' },
  { name: 'Filipenses', chapters: 4, testament: 'NT' },
  { name: 'Colossenses', chapters: 4, testament: 'NT' },
  { name: '1 Tessalonicenses', chapters: 5, testament: 'NT' },
  { name: '2 Tessalonicenses', chapters: 3, testament: 'NT' },
  { name: '1 Timóteo', chapters: 6, testament: 'NT' },
  { name: '2 Timóteo', chapters: 4, testament: 'NT' },
  { name: 'Tito', chapters: 3, testament: 'NT' },
  { name: 'Filemom', chapters: 1, testament: 'NT' },
  { name: 'Hebreus', chapters: 13, testament: 'NT' },
  { name: 'Tiago', chapters: 5, testament: 'NT' },
  { name: '1 Pedro', chapters: 5, testament: 'NT' },
  { name: '2 Pedro', chapters: 3, testament: 'NT' },
  { name: '1 João', chapters: 5, testament: 'NT' },
  { name: '2 João', chapters: 1, testament: 'NT' },
  { name: '3 João', chapters: 1, testament: 'NT' },
  { name: 'Judas', chapters: 1, testament: 'NT' },
  { name: 'Apocalipse', chapters: 22, testament: 'NT' },
];

export default function Biblia() {
  const [versionKey, setVersionKey] = useState('ARC');
  const [bookIndex, setBookIndex] = useState(42);
  const [chapter, setChapter] = useState(1);
  const [loading, setLoading] = useState(false);
  const [versionsCache, setVersionsCache] = useState({});
  const [verses, setVerses] = useState([]);
  const [searchVerse, setSearchVerse] = useState('');
  const [note, setNote] = useState('');

  const selectedBook = BOOKS[bookIndex];

  const chapterOptions = useMemo(
    () => Array.from({ length: selectedBook.chapters }, (_, i) => i + 1),
    [selectedBook]
  );

  const noteStorageKey = useMemo(
    () => `bible-note:${versionKey}:${bookIndex}:${chapter}`,
    [versionKey, bookIndex, chapter]
  );

  const referenceTitle = `${selectedBook.name} ${chapter} (${versionKey})`;
  const normalizedSearch = searchVerse.trim().toLowerCase();
  const filteredVerses = useMemo(() => {
    const mapped = verses.map((text, index) => ({ text, number: index + 1 }));
    if (!normalizedSearch) return mapped;
    return mapped.filter((item) => String(item.text || '').toLowerCase().includes(normalizedSearch));
  }, [verses, normalizedSearch]);
  const isFirstChapter = bookIndex === 0 && chapter === 1;
  const isLastChapter = bookIndex === BOOKS.length - 1 && chapter === BOOKS[BOOKS.length - 1].chapters;

  useEffect(() => {
    const saved = window.localStorage.getItem(noteStorageKey) || '';
    setNote(saved);
  }, [noteStorageKey]);

  const saveNote = () => {
    window.localStorage.setItem(noteStorageKey, note);
    toast.success('Anotação salva.');
  };

  const loadDataForVersion = async (version) => {
    if (versionsCache[version.key]) return versionsCache[version.key];

    const response = await fetch(version.url);
    if (!response.ok) throw new Error('Falha ao carregar versão bíblica.');
    const data = await response.json();
    if (!Array.isArray(data)) throw new Error('Formato inválido da Bíblia.');

    setVersionsCache((prev) => ({ ...prev, [version.key]: data }));
    return data;
  };

  const loadCurrentChapter = async () => {
    const version = VERSIONS.find((v) => v.key === versionKey);
    if (!version) return;

    try {
      setLoading(true);
      setVerses([]);
      const bibleData = await loadDataForVersion(version);
      const chapterVerses = bibleData?.[bookIndex]?.chapters?.[chapter - 1] || [];
      setVerses(Array.isArray(chapterVerses) ? chapterVerses : []);
    } catch (_error) {
      toast.error('Não foi possível carregar este capítulo agora.');
      setVerses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentChapter();
     
  }, [versionKey, bookIndex, chapter]);

  const goPreviousChapter = () => {
    if (chapter > 1) {
      setChapter((c) => c - 1);
      return;
    }
    if (bookIndex > 0) {
      const previousBookIndex = bookIndex - 1;
      setBookIndex(previousBookIndex);
      setChapter(BOOKS[previousBookIndex].chapters);
    }
  };

  const goNextChapter = () => {
    if (chapter < selectedBook.chapters) {
      setChapter((c) => c + 1);
      return;
    }
    if (bookIndex < BOOKS.length - 1) {
      const nextBookIndex = bookIndex + 1;
      setBookIndex(nextBookIndex);
      setChapter(1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-amber-600 to-orange-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Bíblia" subtitle="Leitura por livro, capítulo e versão" showBack />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-4 border">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-amber-700" />
            <p className="text-sm font-semibold text-gray-900">Escolha versão, livro e capítulo</p>
          </div>

          <div className="space-y-2">
            <select
              className="mbv-bible-select h-10 rounded-md border border-gray-300 px-3 text-sm w-full"
              value={versionKey}
              onChange={(e) => setVersionKey(e.target.value)}
            >
              {VERSIONS.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.label}
                </option>
              ))}
            </select>

            <select
              className="mbv-bible-select h-10 rounded-md border border-gray-300 px-3 text-sm w-full"
              value={bookIndex}
              onChange={(e) => {
                setBookIndex(Number(e.target.value));
                setChapter(1);
              }}
            >
              <optgroup label="Antigo Testamento">
                {BOOKS.filter((b) => b.testament === 'AT').map((book) => {
                  const idx = BOOKS.indexOf(book);
                  return (
                    <option key={book.name} value={idx}>
                      {book.name}
                    </option>
                  );
                })}
              </optgroup>
              <optgroup label="Novo Testamento">
                {BOOKS.filter((b) => b.testament === 'NT').map((book) => {
                  const idx = BOOKS.indexOf(book);
                  return (
                    <option key={book.name} value={idx}>
                      {book.name}
                    </option>
                  );
                })}
              </optgroup>
            </select>

            <select
              className="mbv-bible-select h-10 rounded-md border border-gray-300 px-3 text-sm w-full"
              value={chapter}
              onChange={(e) => setChapter(Number(e.target.value))}
            >
              {chapterOptions.map((num) => (
                <option key={num} value={num}>
                  Capítulo {num}
                </option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={goPreviousChapter} disabled={isFirstChapter}>
                Capítulo anterior
              </Button>
              <Button type="button" variant="outline" onClick={goNextChapter} disabled={isLastChapter}>
                Próximo capítulo
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4 border">
          <p className="text-sm font-semibold text-gray-900 mb-3">{referenceTitle}</p>
          <Input
            value={searchVerse}
            onChange={(e) => setSearchVerse(e.target.value)}
            placeholder="Buscar palavra neste capítulo"
            className="mb-3"
          />
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando versículos...
            </div>
          ) : filteredVerses.length === 0 ? (
            <p className="text-sm text-gray-600">Nenhum versículo encontrado para essa busca.</p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
              {filteredVerses.map((verse) => (
                <p key={`${versionKey}-${bookIndex}-${chapter}-${verse.number}`} className="text-base leading-8 text-gray-800">
                  <span className="font-semibold text-amber-700 mr-2">{verse.number}</span>
                  {String(verse.text || '').trim()}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4 border">
          <p className="text-sm font-semibold text-gray-900 mb-2">Anotações de estudo</p>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={6}
            placeholder="Escreva aqui seus aprendizados deste capítulo."
          />
          <Button className="mt-3 w-full" onClick={saveNote}>
            Salvar anotação
          </Button>
        </div>
      </div>
    </div>
  );
}
